import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "../ui/Modal";
import { formatDate, formatTime } from "../../../utils/countryLocaleMap";
import { useWidgetLocale } from "../hooks/useWidgetLocale";
import type { Availability, AlternativeVenueTime } from "../../../common/jsonRpcTypes";
import type { CoreBookingDetails } from "../hooks/useBookingCreate";
import { PersonFill, CalendarEvent, Clock } from "../icons";
import { Tag } from "lucide-react";
import "../../styles/cross-sell-modal.scss";

interface CrossSellModalProps {
  readonly show: boolean;
  readonly availability: Availability;
  readonly venueName: string;
  readonly bookingDate: string;
  readonly bookingTime: string;
  readonly numPeople: number;
  readonly bookingTypeName: string;
  readonly countryCode: string;
  readonly updateBooking: (details: Partial<CoreBookingDetails>) => Promise<void>;
  readonly onEnquiry: () => void;
  readonly onClose: () => void;
  readonly error?: string | null;
}

/**
 * Extract HH:mm from a time string for API submission.
 * Handles both ISO date strings and plain HH:mm values.
 */
function extractTimeForApi(timeStr: string): string {
  if (timeStr.includes("T")) {
    const d = new Date(timeStr);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  }
  return timeStr;
}

/**
 * CrossSellModal — displays alternative time/venue suggestions in a centred
 * popup when the requested booking time is unavailable.
 *
 * Desktop: 600px max-width, centred with dark overlay, scrollable body.
 * Mobile: full-screen modal.
 */
const CrossSellModal: React.FC<CrossSellModalProps> = ({
  show,
  availability,
  venueName,
  bookingDate,
  bookingTime,
  numPeople,
  bookingTypeName,
  countryCode,
  updateBooking,
  onEnquiry,
  onClose,
  error,
}) => {
  const { t } = useTranslation();
  const timeLocale = useWidgetLocale(countryCode);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [selectedVenueId, setSelectedVenueId] = useState<string | null>(null);
  const [selectedVenueType, setSelectedVenueType] = useState<string | { id: string; name: string } | undefined>(
    undefined,
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const availabilityResetToken = JSON.stringify({
    action: availability.action,
    data: availability.data ?? null,
  });

  // Reset selection state when availability data changes (e.g. after selecting
  // an alternative time that itself triggers a new suggest_times response).
  React.useEffect(() => {
    if (!availabilityResetToken) {
      return;
    }

    setSelectedTime(null);
    setSelectedVenueId(null);
    setSelectedVenueType(undefined);
    setUpdateError(null);
  }, [availabilityResetToken]);

  const alternativeTimes = availability.data?.alternative_times ?? [];
  const alternativeVenueTimes = availability.data?.alternative_venue_times ?? [];
  const noAvailabilityAction = availability.data?.no_availability_action;

  const handleSelectTime = (timeStart: string) => {
    setSelectedTime(timeStart);
    setSelectedVenueId(null);
    setSelectedVenueType(undefined);
  };

  const handleSelectVenueTime = (slotStart: string, venueId: string, type?: string | { id: string; name: string }) => {
    setSelectedTime(slotStart);
    setSelectedVenueId(venueId);
    setSelectedVenueType(type);
  };

  const [updateError, setUpdateError] = useState<string | null>(null);

  const handleContinue = async () => {
    if (!selectedTime) return;
    setIsUpdating(true);
    setUpdateError(null);
    try {
      const details: Partial<CoreBookingDetails> = { time: extractTimeForApi(selectedTime) };
      if (selectedVenueId) {
        details.venue_id = selectedVenueId;
        const typeId = typeof selectedVenueType === "object" ? selectedVenueType.id : selectedVenueType;
        if (typeId) {
          details.type = typeId;
        }
      }
      await updateBooking(details);
    } finally {
      setIsUpdating(false);
    }
  };

  // Surface errors from parent state (dispatched by updateBooking on API failure)
  React.useEffect(() => {
    setUpdateError(error ?? null);
  }, [error]);

  if (isUpdating) {
    return (
      <Modal show={show} onHide={onClose} centered dialogClassName="cross-sell-modal">
        <Modal.Body className="cross-sell-modal__body">
          <div className="gcw:flex gcw:justify-center gcw:py-bs-3" aria-live="polite" data-testid="cross-sell-loading">
            <div className="loading-spinner" />
            <span className="gcw:sr-only">{t("loading")}</span>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal show={show} onHide={onClose} centered dialogClassName="cross-sell-modal">
      <Modal.Header closeButton className="cross-sell-modal__header">
        <Modal.Title className="cross-sell-modal__title">
          <h2 className="gcw:text-2xl gcw:font-heading gcw:font-bold gcw:text-text-primary">
            {t("cross_sell_modal_title")}
          </h2>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="cross-sell-modal__body">
        <p className="gcw:text-body gcw:text-text-muted gcw:mb-bs-3">
          {t("cross_sell_modal_description", { venue: venueName })}
        </p>

        {/* Your Selection summary */}
        <div className="cross-sell-modal__selection" data-testid="cross-sell-selection">
          <span className="cross-sell-modal__selection-label">{t("cross_sell_modal_your_selection")}</span>
          <div className="cross-sell-modal__selection-details">
            {bookingDate && (
              <span className="cross-sell-modal__selection-item">
                <CalendarEvent size={16} aria-hidden="true" />
                {formatDate(bookingDate, countryCode, "short")}
              </span>
            )}
            {bookingTime && (
              <span className="cross-sell-modal__selection-item gcw:text-body">
                <Clock size={18} aria-hidden="true" />
                {formatTime(extractTimeForApi(bookingTime), bookingDate, countryCode, timeLocale)}
              </span>
            )}
            {numPeople > 0 && (
              <span className="cross-sell-modal__selection-item gcw:text-body">
                <PersonFill size={18} aria-hidden="true" />
                {numPeople} {numPeople === 1 ? t("booking_details_guest") : t("booking_details_guests")}
              </span>
            )}
            {bookingTypeName && (
              <span className="cross-sell-modal__selection-item gcw:text-body">
                <Tag size={18} aria-hidden="true" />
                {bookingTypeName}
              </span>
            )}
          </div>
        </div>

        {/* Error banner — shown when updateBooking fails */}
        {updateError && (
          <div
            className="gcw:rounded-md gcw:border gcw:border-error gcw:bg-error/10 gcw:p-bs-3 gcw:text-error widget-error-panel gcw:mb-bs-3"
            role="alert"
            data-testid="cross-sell-error"
          >
            <p>{updateError}</p>
          </div>
        )}

        {/* Alternative times */}
        {alternativeTimes.length > 0 && (
          <div className="cross-sell-modal__times" data-testid="cross-sell-alternative-times">
            <h3 className="gcw:font-bold gcw:text-text-primary gcw:mb-2">{t("cross_sell_modal_available_times")}</h3>
            <div className="cross-sell-modal__times-grid" aria-label={t("cross_sell_modal_available_times")}>
              {alternativeTimes.map((alt) => {
                const startLabel = formatTime(extractTimeForApi(alt.start), bookingDate, countryCode, timeLocale);
                const endLabel = formatTime(extractTimeForApi(alt.end), bookingDate, countryCode, timeLocale);
                const timeLabel = `${startLabel} - ${endLabel}`;
                const isSelected = selectedTime === alt.start && !selectedVenueId;

                return (
                  <button
                    key={`${alt.start}-${alt.end}`}
                    type="button"
                    className={`cross-sell-modal__time-pill gcw:font-bold ${isSelected ? "cross-sell-modal__time-pill--selected" : ""}`}
                    disabled={!alt.available}
                    onClick={() => handleSelectTime(alt.start)}
                    aria-label={t("booking_select_time_slot", { time: timeLabel })}
                    aria-pressed={isSelected}
                  >
                    {timeLabel}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Alternative venue times */}
        {alternativeVenueTimes.length > 0 && (
          <div className="cross-sell-modal__venues" data-testid="cross-sell-alternative-venues">
            <h3 className="gcw:font-bold gcw:text-text-primary gcw:mb-2">{t("booking_alternative_venues")}</h3>
            <ul className="gcw:flex gcw:flex-col gcw:gap-2" aria-label={t("booking_alternative_venues")}>
              {alternativeVenueTimes.map((alt: AlternativeVenueTime, idx: number) => {
                const altVenueName = alt.venue?.title ?? "";
                const venueStreet = alt.venue?.street ?? "";
                const venuePostcode = alt.venue?.postcode ?? "";
                const altVenueId = alt.venue?._id ?? "";
                const slots = alt.slots ?? [];

                return (
                  <li
                    key={`venue-${altVenueId || idx}`}
                    className="gcw:border gcw:border-border gcw:rounded-md gcw:p-bs-3"
                  >
                    <div className="gcw:mb-2">
                      <span className="gcw:font-medium gcw:text-text-primary">{altVenueName}</span>
                      {venueStreet && (
                        <span className="gcw:ml-2 gcw:text-text-muted">
                          {venueStreet}
                          {venuePostcode ? `, ${venuePostcode}` : ""}
                          {alt.distance != null ? ` (${alt.distance.toFixed(1)} miles)` : ""}
                        </span>
                      )}
                    </div>
                    {slots.length > 0 && (
                      <div className="cross-sell-modal__times-grid">
                        {slots.map((slot) => {
                          const slotLabel = formatTime(
                            extractTimeForApi(slot.start),
                            bookingDate,
                            countryCode,
                            timeLocale,
                          );
                          const isSelected = selectedTime === slot.start && selectedVenueId === altVenueId;

                          return (
                            <button
                              key={slot.start}
                              type="button"
                              className={`cross-sell-modal__time-pill gcw:font-bold ${isSelected ? "cross-sell-modal__time-pill--selected" : ""}`}
                              disabled={!slot.available}
                              onClick={() => handleSelectVenueTime(slot.start, altVenueId, alt.booking?.type)}
                              aria-label={t("booking_select_venue_time_slot", {
                                venue: altVenueName,
                                time: slotLabel,
                              })}
                              aria-pressed={isSelected}
                            >
                              {slotLabel}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="cross-sell-modal__footer">
        <button
          type="button"
          className="cross-sell-modal__enquiry-btn gcw:font-bold"
          disabled={!selectedTime}
          onClick={handleContinue}
          data-testid="cross-sell-continue-btn"
        >
          {t("cross_sell_modal_continue")}
        </button>
        {noAvailabilityAction === "may_enquire" && (
          <button
            type="button"
            className="cross-sell-modal__enquiry-btn gcw:font-bold"
            onClick={onEnquiry}
            data-testid="cross-sell-enquiry-btn"
          >
            {t("cross_sell_modal_enquiry", {
              time: bookingTime ? formatTime(extractTimeForApi(bookingTime), bookingDate, countryCode, timeLocale) : "",
            })}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default CrossSellModal;
